import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { formatDateWithFormatter, formatNumeral } from '@/utils/function'
import dayjs from 'dayjs'
import { PieChart as PieChartIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'
import { getCompanyRevenue } from '../../../services'
import type { RevenueType } from '..'

interface DensityChartProps {
    filters: {
        gte?: string
        lte?: string
        employee_id?: number[]
    }
    revenueTypes?: RevenueType[]
}

const DensityChart = ({
    filters,
    revenueTypes = ['renewal', 'new_sale', 'upsell'],
}: DensityChartProps) => {
    const { t } = useTranslation()
    const { data, renderState } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.REPORTS_BUSINESS_TARGETS,
            'company-revenue',
            filters.gte,
            filters.lte,
            filters?.employee_id ?? [],
        ],
        queryFn: () =>
            getCompanyRevenue({
                gte: filters.gte,
                lte: filters.lte,
                employee_id: filters?.employee_id ?? [],
            }),
        enabled: !!filters.gte && !!filters.lte,
    })

    // Calculate employee revenue based on selected revenue types
    const employeeRevenue = (() => {
        if (!data) return 0
        let sum = 0
        if (revenueTypes.includes('renewal'))
            sum += data?.employee_revenue?.renewal_revenue || 0
        if (revenueTypes.includes('new_sale'))
            sum += data?.employee_revenue?.new_sale_revenue || 0
        if (revenueTypes.includes('upsell'))
            sum += data?.employee_revenue?.upsell_revenue || 0
        return sum
    })()

    const contributionPercent = employeeRevenue
        ? (employeeRevenue / (data?.total_revenue || 0)) * 100
        : 0

    const renderComponent = renderState()
    if (renderComponent) return renderComponent

    const displayPercent = Math.min(contributionPercent, 100)
    const remaining = 100 - displayPercent

    // Gauge data for semi-circle
    const gaugeData = [
        { name: 'contribution', value: displayPercent },
        { name: 'remaining', value: remaining },
    ]

    const periodLabel = `${formatDateWithFormatter(filters?.gte || '', 'MM/YYYY')} - ${formatDateWithFormatter(filters?.lte || '', 'MM/YYYY')}`
    const monthCount =
        dayjs(filters?.lte).diff(dayjs(filters?.gte), 'month') + 1

    return (
        <div className="flex flex-col items-center justify-center h-full py-4">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <PieChartIcon className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                    {t('employees.performance.revenue_contribution')}
                </span>
            </div>

            {/* Gauge Chart */}
            <div className="relative w-36 h-20 mb-2">
                <ResponsiveContainer width="100%" height={80}>
                    <PieChart>
                        <Pie
                            data={gaugeData}
                            cx="50%"
                            cy="100%"
                            startAngle={180}
                            endAngle={0}
                            innerRadius={45}
                            outerRadius={65}
                            dataKey="value"
                            strokeWidth={0}
                        >
                            <Cell fill="#3b82f6" />
                            <Cell fill="#e5e7eb" />
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
            </div>

            {/* Percentage display */}
            <span className="text-3xl font-bold text-blue-600 mb-2">
                {formatNumeral(contributionPercent, 1)}%
            </span>

            {/* Info badge */}
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                {t('employees.performance.of_total_revenue')}
            </div>

            {/* Period info */}
            <p className="mt-2 text-xs text-gray-500 text-center">
                {periodLabel} ({monthCount} {t('common.months')})
            </p>
        </div>
    )
}

export default DensityChart
